// src/utils/comparisonWindow.ts
//
// The comparison-window date math, in a module with no Mantine and no CSS
// imports — deliberately.
//
// It used to live inside TimeRangeSelector.tsx, which meant
// `scripts/dump-comparison-fixtures.ts` could not import it ("its transitive
// Mantine imports include CSS files that tsx can't parse") and had to
// RE-IMPLEMENT the algorithm instead. The cross-language fixture that pins
// this math against the Python side therefore pinned the *copy*: a drift
// between the copy and the shipped function was invisible, and nothing else
// tested the shipped function at all.
//
// Splitting the pure math out removes the copy rather than maintaining it.
// The script, the component and both test suites now run the same lines.
// TimeRangeSelector re-exports everything here, so no call site changed.

import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isSameYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";
import { enGB } from "date-fns/locale";
import type { ComparisonMode, Coverage } from "@verity/charts";

/** The FINITE list of named presets — every row a picker shows, plus the
 *  `last_*_days` values that exist in `getPresetDates` without a row of their
 *  own, plus `custom`. `TimeRangePreset` below widens it with the rolling
 *  spelling; the exhaustive tables (`PRESET_ALIGNMENT`, the storage guard)
 *  stay keyed on THIS one, because `rolling_<n>_<unit>` is a family of
 *  infinitely many strings and no `Record` can list it. */
export type NamedPreset =
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_14_days"
  | "last_28_days"
  | "last_30_days"
  | "last_90_days"
  | "last_365_days"
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "this_year"
  | "last_year"
  | "custom";

/** What a dashboard period can BE: a named preset or the rolling spelling
 *  `rolling_<n>_<unit>` (W2.3; ADR-0267). The two travel identically —
 *  through `TimeRange.preset`, into the stored period, out to the backend's
 *  `preset` query param — so nothing between the picker and
 *  `resolve_preset_dates` needs a case per N. */
export type TimeRangePreset = NamedPreset | RollingPreset;

/**
 * The presets only a REPORT can name (`services/reports/periods.py`): the
 * dashboard never offers them, but the report resolver runs the same
 * previous-year rule, so they are classified in the same table (plan
 * 2026-09-13-report-builder-parity-workstreams W1.2; ADR-0267) and pinned by
 * the same fixture. Mirror of `REPORT_ONLY_PRESETS` in
 * `services/dashboard_cache_config.py`.
 */
export type ReportOnlyPreset =
  | "wtd"
  | "mtd"
  | "ytd"
  | "last_28_days"
  | RollingFamily;

/** The family a rolling spelling collapses to — complete or including the
 *  current unit (`rolling_n_months_incl`). */
export type RollingFamily = `rolling_n_${RollingUnit}` | `rolling_n_${RollingUnit}_incl`;

/** Every preset the alignment rule classifies, on either surface. These are
 *  FAMILIES: a rolling spelling (`rolling_4_weeks`) and an alias (`wtd`) are
 *  classified through `presetFamily`, never listed per N — which is exactly
 *  why this is built on `NamedPreset` and not on `TimeRangePreset`. */
export type AlignedPreset = NamedPreset | ReportOnlyPreset;

// ---------------------------------------------------------------------------
// The rolling spelling and the aliases — mirror of
// `apps/backend/services/period_vocabulary.py` (W2.0; ADR-0267)
// ---------------------------------------------------------------------------

export type RollingUnit = "days" | "weeks" | "months" | "quarters" | "years";

/** The units, in picker order. */
export const ROLLING_UNITS: readonly RollingUnit[] = ["days", "weeks", "months", "quarters", "years"];

/** `rolling_<n>_<unit>[_incl]`: the STRING spelling of a rolling window — what
 *  a report's `period` column, a dashboard preset and a block's own period
 *  carry. Without `_incl`: whole units ending with the last complete one (N
 *  days up to yesterday, N ISO weeks ending last Sunday, N calendar months /
 *  quarters / years ending at the end of the last one). With `_incl`: the
 *  current unit to date plus the N-1 complete units before it. */
export type RollingPreset =
  | `rolling_${number}_${RollingUnit}`
  | `rolling_${number}_${RollingUnit}_incl`;

/** Upper bound per unit — the same numbers as `ROLLING_MAX` on the backend. */
export const ROLLING_MAX: Record<RollingUnit, number> = {
  days: 366,
  weeks: 156,
  months: 36,
  quarters: 12,
  years: 3,
};

function rollingFamily(unit: RollingUnit, incl: boolean): RollingFamily {
  return incl ? `rolling_n_${unit}_incl` : `rolling_n_${unit}`;
}

// Anchored on both ends with no multiline flag: in JS `$` never matches
// before a trailing newline, so `"rolling_4_weeks\n"` is refused here as it
// is by the backend's `\A…\Z` + fullmatch.
const ROLLING_KEY = /^rolling_([1-9]\d{0,2})_(days|weeks|months|quarters|years)(_incl)?$/;

/** The report's to-date spellings and the window each one IS. Understood on
 *  both surfaces, offered by neither picker (one label per window). */
export const PRESET_ALIASES: Record<"wtd" | "mtd" | "ytd", NamedPreset> = {
  wtd: "this_week",
  mtd: "this_month",
  ytd: "this_year",
};

export type RollingParts = { n: number; unit: RollingUnit; incl: boolean };

/** `"rolling_4_weeks"` → `{ n: 4, unit: "weeks", incl: false }`,
 *  `"rolling_3_months_incl"` → `{ n: 3, unit: "months", incl: true }`; `null`
 *  when `value` is not that spelling, or is one with N above its unit's bound
 *  (the backend distinguishes the two — a validator names the bound — but a
 *  client only ever needs "is this a window I can draw"). */
export function parseRollingPreset(value: unknown): RollingParts | null {
  if (typeof value !== "string") return null;
  const m = ROLLING_KEY.exec(value);
  if (!m) return null;
  const n = Number(m[1]);
  const unit = m[2] as RollingUnit;
  if (n > ROLLING_MAX[unit]) return null;
  return { n, unit, incl: m[3] !== undefined };
}

/** The spelling for `n` `unit`s; the inverse of `parseRollingPreset`.
 *  Throws on a value outside the bound — a picker clamps before calling. */
export function rollingPreset(n: number, unit: RollingUnit, incl = false): RollingPreset {
  if (!Number.isInteger(n) || n < 1 || n > ROLLING_MAX[unit]) {
    throw new RangeError(`a rolling window of ${unit} spans 1 to ${ROLLING_MAX[unit]}, not ${n}`);
  }
  return incl ? `rolling_${n}_${unit}_incl` : `rolling_${n}_${unit}`;
}

const ROLLING_UNIT_NOUN: Record<RollingUnit, [singular: string, plural: string]> = {
  days: ["day", "days"],
  weeks: ["week", "weeks"],
  months: ["month", "months"],
  quarters: ["quarter", "quarters"],
  years: ["year", "years"],
};

const ROLLING_CURRENT: Record<RollingUnit, string> = {
  days: "today",
  weeks: "this week",
  months: "this month",
  quarters: "this quarter",
  years: "this year",
};

const ROLLING_UNIT_LABEL: Record<RollingUnit, string> = {
  days: "Days",
  weeks: "Weeks",
  months: "Months",
  quarters: "Quarters",
  years: "Years",
};

/** The units a rolling period input offers, in order — all five, so a
 *  quarter or year window written through the MCP or the API is one the
 *  picker can also build (plan 2026-10-16 block own period, step 5). */
export const ROLLING_UNIT_OPTIONS: readonly { value: RollingUnit; label: string }[] = ROLLING_UNITS.map(
  (value) => ({ value, label: ROLLING_UNIT_LABEL[value] }),
);

/** The label of the switch that turns `rolling_<n>_<unit>` into its `_incl`
 *  spelling: "Include current month", and "Include today" for days. */
export function rollingIncludeCurrentLabel(unit: RollingUnit): string {
  return unit === "days" ? "Include today" : `Include current ${ROLLING_UNIT_NOUN[unit][0]}`;
}

/** "Last 4 weeks", "Last 3 months incl. this month" — the label a rolling
 *  spelling carries on a picker button, a tile and a report period. The
 *  backend's sentence names (`services/period_names.py`) say the same words. */
export function rollingPresetLabel({ n, unit, incl }: RollingParts): string {
  const base = `Last ${n} ${ROLLING_UNIT_NOUN[unit][n === 1 ? 0 : 1]}`;
  return incl ? `${base} incl. ${ROLLING_CURRENT[unit]}` : base;
}

const ROLLING_COMPLETE_MEANING: Record<RollingUnit, string> = {
  days: "Whole days, up to and including yesterday",
  weeks: "Whole weeks, Monday to Sunday, ending last Sunday",
  months: "Whole calendar months, ending last month",
  quarters: "Whole calendar quarters, ending last quarter",
  years: "Whole calendar years, ending last year",
};

/** What a rolling window covers, in words, without its dates: "Whole
 *  calendar months, ending last month", "This month so far and the 2 whole
 *  months before it". The rolling period input puts the resolved dates
 *  after it. */
export function rollingWindowMeaning({ n, unit, incl }: RollingParts): string {
  if (!incl) return ROLLING_COMPLETE_MEANING[unit];
  const current = ROLLING_CURRENT[unit];
  const soFar = `${current[0].toUpperCase()}${current.slice(1)} so far`;
  if (n === 1) return soFar;
  return `${soFar} and the ${n - 1} whole ${ROLLING_UNIT_NOUN[unit][n === 2 ? 0 : 1]} before it`;
}

/** The name `PRESET_ALIGNMENT` knows `value` by: a rolling spelling collapses
 *  to its family, an alias to the window it names, a classified name is its
 *  own family. `undefined` for anything else — an unknown name, which the
 *  alignment rule reads as calendar (the pre-rule behaviour). */
export function presetFamily(value: unknown): AlignedPreset | undefined {
  if (typeof value !== "string" || !value) return undefined;
  const rolling = parseRollingPreset(value);
  if (rolling) return rollingFamily(rolling.unit, rolling.incl);
  const aliased = (PRESET_ALIASES as Record<string, NamedPreset | undefined>)[value] ?? value;
  return Object.hasOwn(PRESET_ALIGNMENT, aliased) ? (aliased as AlignedPreset) : undefined;
}

// `ComparisonMode` and `Coverage` live in `@verity/charts` since the leg
// merge moved there (plan 2026-10-24-delivery-review-and-the-static-
// renderer, E1b): the static edition merges an edition's legs with it.
export type { ComparisonMode, Coverage } from "@verity/charts";

export type TimeRange = {
  preset: TimeRangePreset;
  startDate: Date;
  endDate: Date;
  comparisonMode: ComparisonMode;
  // Only used when comparisonMode === "custom"
  comparisonStartDate?: Date;
  comparisonEndDate?: Date;
  /** The account's target plan as a SECOND leg beside a window comparison
   *  (plan 2026-09-13-report-builder-parity-workstreams, W1.8; ADR-0267):
   *  every chart and table then draws the window overlay AND the dashed
   *  plan line, every KPI tile two chips. Meaningless with `none` or
   *  `target` (the plan is then the comparison itself) — `getComparisonRequest`
   *  reads it only under a window mode. */
  withTarget?: boolean;
  /** The OTHER shifted period on KPI cards (plan 2026-09-14-the-plan-leg-on-
   *  every-surface-that-still-draws-one, S4/S5; ADR-0267 Besluit B): under
   *  "Previous period" a KPI card also shows last year, under "Previous year"
   *  the previous period — three chips with the target. Meaningless under
   *  `none`, `custom` and `target`; charts and tables never draw it. */
  withOtherPeriod?: boolean;
  /** The preset whose rule the window modes compare by, when it is not
   *  `preset`: set only on a report page's answered range
   *  (`utils/completeDays.ts::answeredRange`). A window cut to its last
   *  complete day is described by no preset, so it compares by the uncut
   *  preset's rule (a cut `this_week` keeps Monday on Monday); `null` is no
   *  preset at all (the calendar rule, as a custom window in an edition).
   *  Absent: `preset`. Never stored: only the author's range is. */
  comparisonRule?: TimeRangePreset | null;
};

/** The rule `range`'s window modes compare by: `comparisonRule` on an
 *  answered range, else its own preset. */
export function comparisonRuleOf(range: TimeRange): TimeRangePreset | undefined {
  if (range.comparisonRule === undefined) return range.preset;
  return range.comparisonRule ?? undefined;
}

/**
 * Which counterpart does "previous year" mean?
 *
 * The question is never "is this window short enough" — a shift by ANY
 * multiple of 7 preserves a window's weekday composition, whatever its length.
 * The question is:
 *
 *     Is this window anchored to a calendar boundary, or does it roll?
 *
 * - Calendar-anchored (August, Q3, 2026): the boundary IS the meaning, so the
 *   counterpart is the same boundary a year earlier.
 * - Rolling or week-anchored (today, this week, the last 30 days): the weekday
 *   composition is the meaning, so the counterpart is 364 days back — 52 whole
 *   weeks, Monday against Monday.
 *
 * This is not a new rule. The Analyst has applied it since 2026-08-16
 * (`services/periodic_analysis/facts_reader.py::_YOY_SHIFT`, on the cadence
 * axis) and we already promise it to customers in the Analyst docs. These are
 * the same two buckets on the preset axis.
 *
 * The lists cover the FULL `AlignedPreset` union — the `TimeRangePreset`
 * union (not just the ten the dropdown shows: `last_7_days`…`last_365_days`
 * are resolvable presets, and a rule defined over the visible ten would drop
 * five rolling windows onto the calendar branch the day someone adds them to
 * `PRESET_OPTIONS`) plus the report-only presets, which the report resolver
 * classifies with the same rule.
 *
 * MUST stay identical to `previous_year_alignment` in
 * `apps/backend/services/dashboard_cache_config.py` — the derived window is
 * part of the comparison cache key, so a divergence does not error, it just
 * never hits.
 */
/**
 * Fail-closed by construction: this is a `Record` over the FULL union, not a
 * pair of sets. Add a preset to `TimeRangePreset` without classifying it here
 * and `tsc` fails — rather than the preset silently taking the calendar branch,
 * which is the one failure mode a set-membership test cannot make loud.
 *
 * `"by-length"` is `custom` only: a hand-picked range states no intent, so the
 * length is the only signal there is (see `previousYearAlignment`).
 */
export const PRESET_ALIGNMENT: Record<
  AlignedPreset,
  "weekday" | "calendar" | "by-length"
> = {
  today: "weekday",
  yesterday: "weekday",
  last_7_days: "weekday",
  last_14_days: "weekday",
  last_30_days: "weekday",
  last_90_days: "weekday",
  last_365_days: "weekday",
  this_week: "weekday",
  last_week: "weekday",
  this_month: "calendar",
  last_month: "calendar",
  this_quarter: "calendar",
  last_quarter: "calendar",
  this_year: "calendar",
  last_year: "calendar",
  custom: "by-length",
  // Report-only presets: week-to-date and the rolling day/week windows keep
  // their weekday composition; month/year-to-date and whole-month windows
  // are calendar-anchored.
  wtd: "weekday",
  last_28_days: "weekday",
  rolling_n_days: "weekday",
  rolling_n_weeks: "weekday",
  mtd: "calendar",
  ytd: "calendar",
  rolling_n_months: "calendar",
  // Whole quarters/years and the including-current windows (plan 2026-10-16
  // block own period): days and weeks roll, the rest is calendar-anchored.
  rolling_n_quarters: "calendar",
  rolling_n_years: "calendar",
  rolling_n_days_incl: "weekday",
  rolling_n_weeks_incl: "weekday",
  rolling_n_months_incl: "calendar",
  rolling_n_quarters_incl: "calendar",
  rolling_n_years_incl: "calendar",
};

/** 52 whole weeks — the shift that makes Monday land on Monday. */
export const WEEKDAY_ALIGNED_YOY_SHIFT_DAYS = 364;

export function previousYearAlignment(
  preset: AlignedPreset | string | undefined,
  startDate: Date,
  endDate: Date,
): "weekday" | "calendar" {
  // Through the FAMILY: `rolling_4_weeks` is a `rolling_n_weeks`, `wtd` is
  // `this_week`. The `Record` stays exhaustive over the families, so tsc
  // still fails on a family added without a classification.
  const family = presetFamily(preset);
  const kind = family ? PRESET_ALIGNMENT[family] : undefined;
  if (kind === "weekday") return "weekday";
  if (
    kind === "by-length" &&
    (differenceInCalendarDays(endDate, startDate) + 1) % 7 === 0
  ) {
    // A HEURISTIC, not a principle, and its hole is known: 1-28 February is
    // 28 days, so a hand-picked February shifts to 2 Feb - 1 Mar instead of to
    // February. Accepted deliberately (Arjen, 2026-08-29) — a hand-picked
    // range carries no stated intent, and "exactly N weeks selected" is the
    // only signal there is. Written down because a heuristic whose known
    // exception is undocumented gets read as a bug and "fixed".
    return "weekday";
  }
  // Unknown or missing preset keeps the behaviour that shipped before this
  // rule existed. A signal that does not arrive must never silently produce a
  // window other than the one the label promises.
  return "calendar";
}

type PreviousPeriodKind = "equal" | "to_date" | "finished" | "rolling_months" | "rolling_to_date";

/**
 * How a family's previous period is built — mirror of
 * `services/period_vocabulary.py::PREVIOUS_PERIOD_RULES` (plan 2026-10-11
 * dashboard period scope, step B). `unit` is calendar months, -1 for a week.
 * By FAMILY, never by window shape: a to-date month on its last day is a whole
 * month by shape and must still compare to-date.
 */
const PREVIOUS_PERIOD_RULES: Record<AlignedPreset, [PreviousPeriodKind, number]> = {
  today: ["equal", 0],
  yesterday: ["equal", 0],
  last_7_days: ["equal", 0],
  last_14_days: ["equal", 0],
  last_28_days: ["equal", 0],
  last_30_days: ["equal", 0],
  last_90_days: ["equal", 0],
  last_365_days: ["equal", 0],
  rolling_n_days: ["equal", 0],
  rolling_n_weeks: ["equal", 0],
  custom: ["equal", 0],
  this_week: ["to_date", -1],
  last_week: ["finished", -1],
  this_month: ["to_date", 1],
  last_month: ["finished", 1],
  this_quarter: ["to_date", 3],
  last_quarter: ["finished", 3],
  this_year: ["to_date", 12],
  last_year: ["finished", 12],
  rolling_n_months: ["rolling_months", 0],
  rolling_n_quarters: ["rolling_months", 0],
  rolling_n_years: ["rolling_months", 0],
  rolling_n_days_incl: ["equal", 0],
  rolling_n_weeks_incl: ["rolling_to_date", -1],
  rolling_n_months_incl: ["rolling_to_date", 1],
  rolling_n_quarters_incl: ["rolling_to_date", 3],
  rolling_n_years_incl: ["rolling_to_date", 12],
  wtd: ["to_date", -1],
  mtd: ["to_date", 1],
  ytd: ["to_date", 12],
};

function equalLengthBefore(startDate: Date, endDate: Date): { start: Date; end: Date } {
  const days = differenceInCalendarDays(endDate, startDate) + 1;
  const end = subDays(startOfDay(startDate), 1);
  const start = subDays(end, days - 1);
  return { start: startOfDay(start), end: endOfDay(end) };
}

const window_ = (start: Date, end: Date) => ({ start: startOfDay(start), end: endOfDay(end) });

/**
 * The previous-period window — mirror of `dashboard_cache_config.py::
 * previous_period_window`, locked by `comparison-dates.json`. Calendar months,
 * never day counts (`subMonths` clamps 31 Mar to 28/29 Feb exactly as
 * `relativedelta`); a window not aligned to the preset's unit, an unknown
 * preset, or a rolling day window: the equal-length window before.
 */
function previousPeriodWindow(
  preset: AlignedPreset | string | undefined,
  startDate: Date,
  endDate: Date,
): { start: Date; end: Date } {
  const family = presetFamily(preset);
  const [kind, unit] = (family && PREVIOUS_PERIOD_RULES[family]) || ["equal", 0];
  const start = startOfDay(startDate);
  const end = startOfDay(endDate);
  if (kind === "equal" || start > end) return equalLengthBefore(startDate, endDate);
  if (kind === "rolling_to_date") return rollingToDateBefore(start, end, unit);
  if (unit === -1) {
    // The window must BE a week (finished) or lie inside one (to-date), as the
    // month branches check their ends (Fable gate B, m6).
    const span = differenceInCalendarDays(end, start);
    if (start.getDay() !== 1 || span >= 7 || (kind === "finished" && span !== 6)) {
      return equalLengthBefore(startDate, endDate);
    }
    return kind === "finished"
      ? window_(subDays(start, 7), subDays(start, 1))
      : window_(subDays(start, 7), subDays(end, 7));
  }
  if (kind === "rolling_months") {
    const lastDay = addDays(end, 1).getDate() === 1;
    if (start.getDate() !== 1 || !lastDay) return equalLengthBefore(startDate, endDate);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
    return window_(subMonths(start, months), subDays(start, 1));
  }
  const unitStart = start.getDate() === 1 && start.getMonth() % unit === 0;
  if (!unitStart) return equalLengthBefore(startDate, endDate);
  if (kind === "finished") {
    const unitEnd = subDays(addMonthsExact(start, unit), 1);
    if (differenceInCalendarDays(end, unitEnd) !== 0) return equalLengthBefore(startDate, endDate);
    return window_(subMonths(start, unit), subDays(start, 1));
  }
  // to-date
  if (end >= addMonthsExact(start, unit)) return equalLengthBefore(startDate, endDate);
  return window_(subMonths(start, unit), subMonths(end, unit));
}

/**
 * Previous period of an including-current window — mirror of
 * `_rolling_to_date_before`: the same to-date shape one span earlier, the span
 * read off the dates in units of the family, clamped like `this_month`'s. A
 * window not starting on a unit boundary keeps the equal-length window before.
 */
function rollingToDateBefore(start: Date, end: Date, unit: number): { start: Date; end: Date } {
  if (unit === -1) {
    if (start.getDay() !== 1) return equalLengthBefore(start, end);
    const shift = 7 * (Math.floor(differenceInCalendarDays(end, start) / 7) + 1);
    return window_(subDays(start, shift), subDays(end, shift));
  }
  if (start.getDate() !== 1 || start.getMonth() % unit !== 0) return equalLengthBefore(start, end);
  const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
  const span = Math.ceil(months / unit) * unit;
  return window_(subMonths(start, span), subMonths(end, span));
}

/** `start + months` for a unit start (day 1), so no clamp applies. */
function addMonthsExact(start: Date, months: number): Date {
  return subMonths(start, -months);
}

/**
 * The shifted window for one of the two WINDOW modes — the one
 * implementation `getComparisonDates` runs and the fixture generator
 * (`scripts/dump-comparison-fixtures.ts`) calls for every preset, report-only
 * ones included. Mirror of `previous_year_window` /
 * `_compute_comparison_dates` on the backend.
 */
export function comparisonWindowFor(
  mode: "previous_period" | "previous_year",
  preset: AlignedPreset | string | undefined,
  startDate: Date,
  endDate: Date,
): { start: Date; end: Date } {
  if (mode === "previous_period") {
    return previousPeriodWindow(preset, startDate, endDate);
  }
  if (previousYearAlignment(preset, startDate, endDate) === "weekday") {
    return {
      start: startOfDay(subDays(startDate, WEEKDAY_ALIGNED_YOY_SHIFT_DAYS)),
      end: endOfDay(subDays(endDate, WEEKDAY_ALIGNED_YOY_SHIFT_DAYS)),
    };
  }
  const start = subYears(startDate, 1);
  const end = subYears(endDate, 1);
  return { start: startOfDay(start), end: endOfDay(end) };
}

/**
 * Compute comparison dates from a TimeRange. Pure function — derives
 * dates from the mode and main date range, not stored in state.
 */
export function getComparisonDates(
  range: TimeRange
): { start: Date; end: Date } | null {
  const { comparisonMode, startDate, endDate } = range;

  switch (comparisonMode) {
    case "none":
      return null;

    case "previous_period":
    case "previous_year":
      return comparisonWindowFor(comparisonMode, comparisonRuleOf(range), startDate, endDate);

    case "custom": {
      if (range.comparisonStartDate && range.comparisonEndDate) {
        return {
          start: startOfDay(range.comparisonStartDate),
          end: endOfDay(range.comparisonEndDate),
        };
      }
      return null;
    }

    default:
      return null;
  }
}


/** The days a comparison total sums; `start`/`end` are null exactly when
 *  `coverage` is `empty`. */
export type CoveredWindow = { coverage: Coverage; start: Date | null; end: Date | null };

const dayKey = (d: Date): string => format(d, "yyyy-MM-dd");

/** `previous_year_period_end` in `services/period_axis.py`: `periodEnd`
 *  shifted by the previous-year leg's OWN rule, read off `compStart` (one
 *  calendar year, or 364 days); null when `compStart` is not a previous-year
 *  leg of `mainStart`. */
function previousYearPeriodEnd(periodEnd: Date, mainStart: Date, compStart: Date): Date | null {
  if (dayKey(compStart) === dayKey(subYears(mainStart, 1))) return subYears(periodEnd, 1);
  if (dayKey(compStart) === dayKey(subDays(mainStart, WEEKDAY_ALIGNED_YOY_SHIFT_DAYS))) {
    return subDays(periodEnd, WEEKDAY_ALIGNED_YOY_SHIFT_DAYS);
  }
  return null;
}

/**
 * The part of the comparison window a TOTAL may sum: the counterpart of the
 * days `[mainStart, min(mainEnd, through)]` the main total has data for.
 * Twin of `covered_comparison_window` in `services/period_axis.py`, pinned by
 * `apps/backend/tests/fixtures/comparison-covered.json` (ADR-0310). Read the
 * Python docstring for the rules; this function must not grow its own.
 *
 * The requested window stays the cache identity - this only names the days
 * a total covers (the picker's "vs …" label, a KPI card's hover).
 */
/**
 * The day-shift of a CALENDAR previous-period leg, read off the dates — twin of
 * `period_axis.py::calendar_unit_shift` (plan 2026-10-11 dashboard period
 * scope, step B). Returns a function mapping a main day to its counterpart, or
 * null when the leg is not one: the same days of the previous unit (to-date,
 * `day − 7 days` / `day − k months`) or the whole previous unit (finished).
 */
function calendarUnitCounterpart(
  mainStart: Date, mainEnd: Date, compStart: Date, compEnd: Date,
): ((day: Date) => Date) | null {
  const same = (a: Date, b: Date) => dayKey(a) === dayKey(b);
  if (mainStart.getDay() === 1 && same(compStart, subDays(mainStart, 7)) && same(compEnd, subDays(mainEnd, 7))) {
    return (day) => subDays(day, 7);
  }
  if (mainStart.getDate() !== 1) return null;
  for (const months of [1, 3, 12]) {
    if (!same(compStart, subMonths(mainStart, months))) continue;
    if (same(compEnd, subMonths(mainEnd, months))) return (day) => subMonths(day, months);
    const wholeUnit = same(mainEnd, subDays(subMonths(mainStart, -months), 1));
    if (wholeUnit && same(compEnd, subDays(mainStart, 1))) {
      return (day) => {
        const shifted = subMonths(day, months);
        return dayKey(shifted) > dayKey(compEnd) ? compEnd : shifted;
      };
    }
  }
  return null;
}

export function coveredComparisonWindow(
  mainStart: Date,
  mainEnd: Date,
  compStart: Date,
  compEnd: Date,
  through: Date | null,
): CoveredWindow {
  if (!through) return { coverage: "unknown", start: compStart, end: compEnd };

  let counterpart: (day: Date) => Date | null;
  const pyEnd = previousYearPeriodEnd(mainEnd, mainStart, compStart);
  if (pyEnd && dayKey(pyEnd) === dayKey(compEnd)) {
    counterpart = (day) => previousYearPeriodEnd(day, mainStart, compStart);
  } else {
    // A calendar leg before an equal-length one: Q4 vs Q3 is both (92 days
    // each), and its counterpart is `day − shift` (ADR-0384 §3), which is
    // what the chart pairing uses too (Fable gate B, m3).
    const calendar = calendarUnitCounterpart(mainStart, mainEnd, compStart, compEnd);
    if (calendar) {
      counterpart = calendar;
    } else if (
      dayKey(compEnd) === dayKey(subDays(mainStart, 1)) &&
      differenceInCalendarDays(compEnd, compStart) === differenceInCalendarDays(mainEnd, mainStart)
    ) {
      counterpart = (day) => addDays(startOfDay(compStart), differenceInCalendarDays(day, mainStart));
    } else {
      return { coverage: "unknown", start: compStart, end: compEnd };
    }
  }

  if (dayKey(through) >= dayKey(mainEnd)) return { coverage: "full", start: compStart, end: compEnd };
  if (dayKey(through) < dayKey(mainStart)) return { coverage: "empty", start: null, end: null };
  const end = counterpart(through);
  return { coverage: "clipped", start: compStart, end: end ? endOfDay(end) : end };
}

/**
 * The compCache coordinate a target leg is stored under.
 *
 * The comparison cache is keyed by (item, main range, comparison start,
 * comparison end) — a target has no comparison range, so it needs a stable
 * stand-in. A constant works because the leg is fully determined by the MAIN
 * range plus the account's active plan, both of which are already in the key
 * or invalidated globally: switching plans wipes the caches the same way a
 * refresh does.
 *
 * It must not collide with a real date, and "target" cannot be one.
 */
export const TARGET_COMPARISON_KEY = "target";

/** Everything a caller needs to ask for, cache and merge a comparison leg. */
export type ComparisonRequest = {
  /** The comparison window. Null for "none" and an unfinished custom range. */
  dates: { start: Date; end: Date } | null;
  /** A comparison window is requested at all: the gate to branch on. */
  active: boolean;
  /** compCache coordinates — the ISO window. Empty strings when no leg is
   *  active (never used in that case). */
  cacheStart: string;
  cacheEnd: string;
  /** The plan as a second leg beside the window (W1.8): the refresh asks
   *  `comparison_mode: "target"` NEXT TO the dates, the GET `with_target`,
   *  and the leg is cached under TARGET_COMPARISON_KEY for the same main
   *  range. False whenever there is no window leg. */
  withTarget: boolean;
  /** The other shifted period for KPI cards (S4): the window of the mode the
   *  viewer did NOT pick, through the same `comparisonWindowFor`. Null unless
   *  `range.withOtherPeriod` under "Previous period" or "Previous year". */
  other: OtherComparisonRequest | null;
};

/** The second shifted window a KPI card asks for beside the first. */
export type OtherComparisonRequest = {
  /** The dashboard's name for the other window. */
  mode: "previous_period" | "previous_year";
  dates: { start: Date; end: Date };
  cacheStart: string;
  cacheEnd: string;
};

/**
 * One derivation of the comparison legs, so every place that asks for one
 * (cache reads and writes, the refresh and bulk payloads, the spec merge,
 * the staleness check, the cache-hit rule) cannot disagree about which legs
 * are active or what window each one covers.
 */
export function getComparisonRequest(range: TimeRange): ComparisonRequest {
  const dates = getComparisonDates(range);
  if (dates) {
    return {
      dates,
      active: true,
      cacheStart: format(dates.start, "yyyy-MM-dd"),
      cacheEnd: format(dates.end, "yyyy-MM-dd"),
      // The plan beside the window (W1.8): only a window can carry it.
      withTarget: range.withTarget === true,
      other: otherComparisonFor(range),
    };
  }
  return { dates: null, active: false, cacheStart: "", cacheEnd: "", withTarget: false, other: null };
}

/** The other shifted window beside a window mode, whether or not the viewer
 *  asked for it — what the picker offers (and dates) on its KPI checkbox. */
export type OtherPeriodWindow = {
  /** The mode the viewer did NOT pick. */
  mode: "previous_period" | "previous_year";
  dates: { start: Date; end: Date };
  /** The other window covers exactly the days the primary one does (Last
   *  Year and This Year, where the previous calendar unit IS the previous
   *  year): asking for it would draw the same comparison twice, so it is
   *  never requested (plan 2026-10-11 dashboard period scope, C1). */
  sameAsPrimary: boolean;
};

/**
 * The other shifted window for `range`'s mode, ignoring `withOtherPeriod`.
 * Null under `none` and `custom`: only "Previous period" and "Previous year"
 * have a counterpart.
 */
export function otherPeriodWindow(range: TimeRange): OtherPeriodWindow | null {
  const mode =
    range.comparisonMode === "previous_period"
      ? "previous_year"
      : range.comparisonMode === "previous_year"
        ? "previous_period"
        : null;
  if (!mode) return null;
  const primaryMode = mode === "previous_year" ? "previous_period" : "previous_year";
  const rule = comparisonRuleOf(range);
  const primary = comparisonWindowFor(primaryMode, rule, range.startDate, range.endDate);
  const dates = comparisonWindowFor(mode, rule, range.startDate, range.endDate);
  const sameAsPrimary =
    dayKey(primary.start) === dayKey(dates.start) && dayKey(primary.end) === dayKey(dates.end);
  return { mode, dates, sameAsPrimary };
}

/** The other leg a KPI card asks for: only when the viewer turned it on and
 *  it is not the primary window again. Every reader (cache keys, refresh and
 *  bulk payloads, the hit rule) goes through `getComparisonRequest`, so a
 *  deduped leg is neither asked for, cached nor waited on. */
function otherComparisonFor(range: TimeRange): OtherComparisonRequest | null {
  if (range.withOtherPeriod !== true) return null;
  const other = otherPeriodWindow(range);
  if (!other || other.sameAsPrimary) return null;
  return {
    mode: other.mode,
    dates: other.dates,
    cacheStart: format(other.dates.start, "yyyy-MM-dd"),
    cacheEnd: format(other.dates.end, "yyyy-MM-dd"),
  };
}

/**
 * The comparison-cache keys of EVERY leg this request asks for: the window,
 * the other period on a metric card, and the plan beside the window. A tile is servable from
 * the client cache only when each of them is cached — a hit on the window alone
 * used to paint a tile without its plan chip and never ask for it (plan
 * 2026-09-14-the-plan-leg-on-every-surface-that-still-draws-one, S2). The one
 * list every cache-hit check reads (`comparisonLegsCached`), so the manual
 * refresh, both bulk phases and the stale-tile check cannot disagree.
 */
export function comparisonLegCacheKeys(
  comp: ComparisonRequest,
  /** The tile's chart type: only a metric card asks for the other period
   *  (S4), so only a metric card needs it cached. */
  chartType?: string,
): Array<{ start: string; end: string }> {
  const keys: Array<{ start: string; end: string }> = [];
  if (comp.active) keys.push({ start: comp.cacheStart, end: comp.cacheEnd });
  if (comp.other && chartType === "metric_card") {
    keys.push({ start: comp.other.cacheStart, end: comp.other.cacheEnd });
  }
  if (comp.withTarget) keys.push({ start: TARGET_COMPARISON_KEY, end: TARGET_COMPARISON_KEY });
  return keys;
}

/**
 * The window `rolling_<n>_<unit>[_incl]` names at `now` — mirror of
 * `_rolling_window_dates` in `apps/backend/services/dashboard_cache_config.py`,
 * itself the inclusive spelling of `services/reports/periods.py::_rolling_window`.
 * Note the complete `days` window ends YESTERDAY, unlike `last_N_days`, which
 * ends today; with `incl` it ends today and IS that window.
 */
export function rollingWindowDates(
  now: Date,
  { n, unit, incl }: RollingParts,
): { startDate: Date; endDate: Date } {
  const today = startOfDay(now);
  const unitStart =
    unit === "days"
      ? today
      : unit === "weeks"
        ? startOfWeek(today, { weekStartsOn: 1 })
        : unit === "months"
          ? startOfMonth(today)
          : unit === "quarters"
            ? startOfQuarter(today)
            : startOfYear(today);
  const back = incl ? n - 1 : n;
  const startDate =
    unit === "days"
      ? subDays(unitStart, back)
      : unit === "weeks"
        ? subWeeks(unitStart, back)
        : subMonths(unitStart, back * (unit === "months" ? 1 : unit === "quarters" ? 3 : 12));
  const endDate = incl ? endOfDay(now) : endOfDay(subDays(unitStart, 1));
  return { startDate, endDate };
}

/** "1 Jan – 31 Dec 2024": a window with its year, written once when both
 *  ends share it. The period picker's comparison tooltip and the rolling
 *  period input's help line both name a window this way. */
export function formatPeriodDates(start: Date, end: Date): string {
  const s = isSameYear(start, end)
    ? format(start, "d MMM", { locale: enGB })
    : format(start, "d MMM yyyy", { locale: enGB });
  return `${s} – ${format(end, "d MMM yyyy", { locale: enGB })}`;
}

/**
 * Calculate start and end dates for a given preset on `now`.
 *
 * `now` defaults to the browser's clock (`new Date()`), so a test pins it with
 * `vi.setSystemTime`; a report page passes its own day instead (`report_today`,
 * `utils/completeDays.ts`). `"custom"` has no dates of its own and falls back
 * to the last 30 days — callers that carry real custom dates must branch
 * BEFORE calling this.
 */
export function getPresetDates(
  preset: TimeRangePreset,
  now: Date = new Date(),
): { startDate: Date; endDate: Date } {
  const today = startOfDay(now);

  // The rolling spelling, BEFORE the switch: `rolling_<n>_<unit>` is a family
  // of strings, so the `default` arm would swallow it and silently hand back
  // the last 30 days.
  const rolling = parseRollingPreset(preset);
  if (rolling) return rollingWindowDates(now, rolling);

  switch (preset) {
    case "today":
      return { startDate: today, endDate: endOfDay(now) };

    case "yesterday": {
      const yesterday = subDays(today, 1);
      return { startDate: yesterday, endDate: endOfDay(yesterday) };
    }

    case "last_7_days":
      return { startDate: subDays(today, 6), endDate: endOfDay(now) };

    case "last_14_days":
      return { startDate: subDays(today, 13), endDate: endOfDay(now) };

    case "last_28_days":
      return { startDate: subDays(today, 27), endDate: endOfDay(now) };

    case "last_30_days":
      return { startDate: subDays(today, 29), endDate: endOfDay(now) };

    case "last_90_days":
      return { startDate: subDays(today, 89), endDate: endOfDay(now) };

    case "last_365_days":
      return { startDate: subDays(today, 364), endDate: endOfDay(now) };

    case "this_week":
      return {
        startDate: startOfWeek(now, { weekStartsOn: 1 }),
        endDate: endOfDay(now),
      };

    case "last_week": {
      const lastWeek = subWeeks(now, 1);
      return {
        startDate: startOfWeek(lastWeek, { weekStartsOn: 1 }),
        endDate: endOfWeek(lastWeek, { weekStartsOn: 1 }),
      };
    }

    case "this_month":
      return {
        startDate: startOfMonth(now),
        endDate: endOfDay(now),
      };

    case "last_month": {
      const lastMonth = subMonths(now, 1);
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth),
      };
    }

    case "this_quarter":
      return {
        startDate: startOfQuarter(now),
        endDate: endOfDay(now),
      };

    case "last_quarter": {
      const lastQuarter = subQuarters(now, 1);
      return {
        startDate: startOfQuarter(lastQuarter),
        endDate: endOfQuarter(lastQuarter),
      };
    }

    case "this_year":
      return {
        startDate: startOfYear(now),
        endDate: endOfDay(now),
      };

    case "last_year": {
      const lastYear = subYears(now, 1);
      return {
        startDate: startOfYear(lastYear),
        endDate: endOfYear(lastYear),
      };
    }

    case "custom":
    default:
      return { startDate: subDays(today, 29), endDate: endOfDay(now) };
  }
}

// Export helper to get initial range. Default preset is `last_month` for
// back-compat; callers in dashboard flows should pass the platform default
// from `useDashboardDefaults()` so a backend-side change in the default
// range automatically propagates without a frontend bump.
export function getDefaultTimeRange(
  preset: TimeRangePreset = "last_month",
): TimeRange {
  const { startDate, endDate } = getPresetDates(preset);
  return {
    preset,
    startDate,
    endDate,
    comparisonMode: "previous_year",
  };
}
